import React, { useState } from 'react';
import { Input, Modal, Form, InputRef, Collapse } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsFolderCreateOpen } from 'store/slices/dataRoomSlice';
import classes from './NewFolderModal.module.scss';
import { useTranslation } from 'react-i18next';
import api from 'api';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { CaretRightOutlined } from '@ant-design/icons';
import { IGroup } from 'interfaces';
import fetchGroupsAndUsers, { defaultGroups } from 'store/reducers/usersGroupCreator';
import { allPermissions } from 'helpers/createPermissionTableData';
import cn from 'classnames';
import { addNewFolder } from 'store/reducers/documentsSocketsCreator';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';
import { getTheme } from 'helpers/getTheme';
import PermissionsByFolder from 'components/PermissionsByFolder/PermissionsByFolder';

type NewFolderModalType = {
  roomId: string;
  open: boolean;
};

export default function NewFolderModal ({ roomId, open }: NewFolderModalType) {
  const [form] = Form.useForm();
  const textInput = React.useRef<InputRef | null>(null);

  const [isFolderCreating, setIsfolderCreating] = useState<boolean>(false);
  const [permissionsByRole, setPermissionsByRole] = React.useState<IGroup[]>([]);
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const [folderNameValue, setFolderNameValue] = useState<string>('');

  const { userFoldersPermissions } = useAppSelector((store) => store.permissions);
  const { currentFolder } = useAppSelector((store) => store.documents);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { groups, users, isGroupsAndUsersLoading } = useAppSelector((store) => store.dataUsers);
  const { userData } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const currentFolderId = currentFolder[0] ? currentFolder[currentFolder.length - 1]?.id : null;
  const needToSetPermissions = currentFolderId === null && activeKey !== 'permissions';  

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  React.useEffect(() => {
    textInput.current?.focus();
  }, []);

  React.useEffect(() => {
    !isGroupsAndUsersLoading && !groups[0] && dispatch(fetchGroupsAndUsers({room_id: dataRoom?.id!}));
  }, [isGroupsAndUsersLoading, groups])

  React.useEffect(() => {
    const meInThisRoom = users.find(user => user.email === userData?.email);
    const preparedAndSortGroups = groups[0] && groups
      .map(group => {        
        const grantAllPermissions = group.name === 'full_administrator' || meInThisRoom?.group[0] === group.group_id;
        const localizedGroupName = defaultGroups.includes(group.name!) ? t(`Users.table.${group.name}`) : group.name;
        const nameWithPrompt = meInThisRoom?.group[0] === group.group_id ? `${localizedGroupName} (${t('Documents.modals.newFolder.myRole')})` : localizedGroupName;        
        return {
          ...group,
          name: nameWithPrompt,
          group_name: localizedGroupName,
          permissions: grantAllPermissions ? allPermissions : [],
          can_edit_permissions: group.name === 'full_administrator' ? false : true,
          type: 'role',
        }
      })
      .sort((a, b) => {
        const includeAName = defaultGroups.includes(String(a.first_name));
        const includeBName = defaultGroups.includes(String(b.first_name))
        if (includeBName && includeAName){
          return defaultGroups.indexOf(String(a.first_name)) - defaultGroups.indexOf(String(b.first_name));
        }
        if(includeAName) return -1;
        if(includeBName) return 1;
        return String(a.first_name).localeCompare(String(b.first_name))
      })      
    
    groups[0] && setPermissionsByRole(preparedAndSortGroups);
  }, [groups]);

  const onFolderCreate = async (values: any) => {
    let isNeedToWaitForPermissions = currentFolderId === null ? permissionsByRole[0] : true;
    
    if (isNeedToWaitForPermissions && !isFolderCreating) {
      setIsfolderCreating(true);

      const trimedFolderName = values.folder_name?.trim();
      
      try {
        const { data: { id, name }} = await api.createFolder({
          name: trimedFolderName,
          room: roomId,
          folder_id: currentFolderId!,
          details: {folder_name: trimedFolderName}
        });
        messageApi?.success(t('Documents.success.createFolder'));
  
        const rolesWithPermissions = permissionsByRole.filter(role => role.permissions && role.permissions[0]);
  
        await Promise.all(rolesWithPermissions.map(group => {
          return api.setFolderPermission({
            permission_types: group.permissions as string[],
            folder_id: id!,
            room_id: dataRoom?.id!,
            group_id: group.id!,
            details: {
              before: [],
              after: group.permissions,
              folder_id: id!,
              folder_name: name,
              group_name: group.group_name,
              group_role: group.group_name,
              group_id: group.id,
            }
          })
          .then(value => {
            messageApi?.success(`${t('Documents.success.permissionsSuccessAssigned')} '${group.group_name}'`);
          })
          .catch(e => {
            messageApi?.warning(`${t('Documents.error.permissionsFailedRole')} '${group.group_name}'`);
          })
        }))      
        dispatch(setIsFolderCreateOpen(false));
  
        const meInThisRoom = users.find(user => user.email === userData?.email);      
        const myRolePermissions = permissionsByRole.find(group => group.id === meInThisRoom?.group[0]);      
        const newFolderWithNeededPermissions = {
          id,
          name,
          can_edit_permissions: true,
          type: 'folder',
          key: `folder ${id} ${(userFoldersPermissions?.length || 0) + 1}`,
          children: null,
          clicked: false,
          permissions: myRolePermissions?.permissions
        };

        dispatch(addNewFolder(newFolderWithNeededPermissions));
        
        // добавляем все права пользователю на только что созданную папку в redux, если это корневая папка
        // currentFolderId === null && dispatch(setUserPermissions({
        //   documents: [],
        //   rootFolder: userRootPermissions,
        //   folders: [
        //     ...userFoldersPermissions || [],
        //     newFolderWithNeededPermissions
        //   ],
        // }));
  
      } catch (err) {
        const error = err as any;
        if (error?.response?.data?.message === 'Folder with this name already exists') {
          messageApi?.error(t('Documents.error.existsFolder'));
          return;
        }
        if (error?.response?.data?.message === 'Folder with this name already exists at this level') {
          messageApi?.error(t('Documents.error.existsFolderAtThisLevel'));
          return;
        }
        messageApi?.error(error.message || error.response?.detail[0].msg || t('Documents.error.newFolder'));
      } finally {
        setIsfolderCreating(false);
      }
    }
  };
  const onCancelClick = () => dispatch(setIsFolderCreateOpen(false));

  const okAction = () => {
    if (needToSetPermissions) {      
      setActiveKey('permissions')
    } else {
      form.submit()
    }
  }

  const handleFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      form.setFieldValue('folder_name', filteredValue);
      setFolderNameValue(filteredValue);
    });
  };

  const handleFolderNamePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, folderNameValue, (filteredValue) => {
      form.setFieldValue('folder_name', filteredValue);
      setFolderNameValue(filteredValue);
    });
  };
    
  return (
    <Modal
      transitionName={ !groups[0] ? undefined : '' }
      title={t('Documents.modals.newFolder.title')}
      centered
      mask={{ closable: false }}
      width={450}
      open={open}
      closable={false}
      onOk={okAction}
      onCancel={onCancelClick}
      className={cn(
        classes.createFolderModal,
        getTheme(),
      )}
      okButtonProps={{
        loading: isFolderCreating || isGroupsAndUsersLoading,
        disabled: (!needToSetPermissions && (currentFolderId === null && !permissionsByRole[0])) || isGroupsAndUsersLoading
      }}
      okText={needToSetPermissions ? t('Documents.modals.newFolder.next') : t('Documents.modals.newFolder.create')}
      cancelText={t('main.cancelButton')}
    >
      <Form form={form} onFinish={(values) => needToSetPermissions ? setActiveKey('permissions') : onFolderCreate(values)}>
        <Form.Item
          className={classes.folderName}
          name='folder_name'
          rules={[{
            required: true,
            whitespace: true,
            message: t('Documents.modals.newFolder.enter_folder_name')}
          ]}
        >
          <Input
            ref={textInput}
            count={{
              show: true,
              max: 150,
            }}
            maxLength={150}
            placeholder={t('Documents.modals.newFolder.placeholder')}
            value={folderNameValue}
            onChange={handleFolderNameChange}
            onPaste={handleFolderNamePaste}
          />
        </Form.Item>
      </Form>

      {currentFolderId === null && <Collapse
        ghost
        className={classes.parentCollapse}
        onChange={(keys) => setActiveKey(keys[0])}
        activeKey={activeKey}
        expandIconPlacement='end'
        expandIcon={({ isActive }) => <CaretRightOutlined className={classes.expandIcon} rotate={isActive ? 90 : 0} />}
        items={[{
          key: 'permissions',
          label: <div className={classes.collapseTitle}>{t('Documents.modals.newFolder.setPermissions')}</div>,
          children: <div className={classes.childWrap}>
            <PermissionsByFolder
              isLoading={isGroupsAndUsersLoading}
              permissionsOfFolder={permissionsByRole}
              setPermissionsOfFolder={setPermissionsByRole}
              parent={'newFolder'}
            />
          </div>
        }]}
      />}

      {isFolderCreating &&
        <UploadingComponent />
      }
    </Modal>
  );
};
